/**
 * Deskey Session (audio-inbox item) status rules behind the Content Studio
 * tiles — Synced, Processing, Drafts, Published.
 *
 * SHARED SOURCE: copied into teacher-mobile by scripts/sync-rich-content.js
 * so the web dashboard and the mobile Studio screen count identically —
 * edit here, then run that script.
 */

// Smart-note phases that mean the auto-sync pipeline is still running. The
// session's own `status` flips to 'ready' right after transcription, so
// multimodal items must also be checked against smart_note_status.
export const SMART_NOTE_ACTIVE = ['queued', 'transcribing', 'ocr_running', 'fusing'];

export function isSmartNoteBusy(item) {
  return !!item.auto_sync_multimodal && SMART_NOTE_ACTIVE.includes(item.smart_note_status);
}

// Teacher already clicked "Generate Session" and it hasn't finished: first
// fusion runs (no draft yet), then the draft's lesson framework is authored
// in the background.
export function isGeneratingSession(item) {
  return !!item.auto_sync_multimodal && (
    (item.smart_note_status === 'fusing' && !item.draft_id) || item.draft_framework_status === 'generating'
  );
}

export function isProcessingItem(item) {
  return item.status === 'uploaded' || item.status === 'transcribing' || isSmartNoteBusy(item) || isGeneratingSession(item);
}

// A synced session is "raw" until a lesson draft is made from it: it holds
// only its transcript, audio and/or board photos. Once a draft exists
// (draft_id set, status drafted/published) it counts under Drafts/Published.
export function isRawSession(item) {
  return !item.draft_id && item.status !== 'drafted' && item.status !== 'published' && !isSkippedSession(item);
}

// The teacher chose "Skip session" (usually on a recording judged unrelated to
// what they teach). It stays listed with a Skipped tag but isn't counted.
export function isSkippedSession(item) {
  return !!item.skipped;
}

// How a session got its class: picked by the teacher, auto-routed from what
// the lesson is about (backend transcript_relevance.apply_auto_routing), or
// matched from the transcript (spoken class code / subject / class name).
export function routingLabel(item) {
  if (item.detected_class_method === 'manual') return 'Manually routed';
  if (item.detected_class_method === 'content_check') return 'Auto-routed from lesson content';
  return `Auto-matched · ${Math.round((item.detected_class_confidence || 0) * 100)}%`;
}

export function countRawSessions(items) {
  return items.filter(isRawSession).length;
}

// Board photo OCR (board_capture_routes.process_board_capture) moves
// pending -> processing -> done | failed. Both running states mean "still
// reading" — polling only on "pending" stops as soon as OCR starts.
export const BOARD_OCR_ACTIVE = ['pending', 'processing'];

export function isBoardOcrBusy(capture) {
  return BOARD_OCR_ACTIVE.includes(capture?.ocr_status);
}

/** The four Content Studio tiles: Synced/Processing from the audio-inbox
 * items, Drafts/Published from GET /teacher/studio/dashboard `stats`. */
export function studioCounts(items, stats) {
  return {
    synced: countRawSessions(items),
    processing: items.filter(isProcessingItem).length,
    drafts: stats?.drafts || 0,
    published: stats?.published || 0,
  };
}
